import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { Session } from "@insite/client-framework/Services/SessionService";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { RecentShipmentItemInfoType } from "@stoneage/App/Store/Reducers/xRecentShipments";
import {
    getBillToCustomerNumber,
    trackAsImpersonation,
    trackGaShipmentDetailsEvent,
} from "@stoneage/App/Utilities/DataLayer";
import InvoiceDetailPageTypeLink from "@stoneage/Overrides/Components/InvoiceDetailPageTypeLink";
import { parseBoolean } from "@stoneage/XDL/Utilities";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    recentShipmentItem: RecentShipmentItemInfoType;
    extendedStyles?: RecentShipmentSummaryCardStyles;
    hasAccessToOrderHistory: boolean;
    hasAccessToInvoiceHistory: boolean;
    session: Session;
}

type Props = OwnProps;

export interface RecentShipmentSummaryCardStyles {
    itemHeadingAndText?: SmallHeadingAndTextStyles;
    dataTableCells?: DataTableCellBaseProps;
    dataCell?: InjectableCss;
}

export const recentShipmentSummaryCardStyles: RecentShipmentSummaryCardStyles = {
    dataCell: {
        css: css`
            &&& {
                a span {
                    color: ${getColor("common.ui1")};
                    font-weight: bold;
                    &:hover,
                    &:focus {
                        color: ${getColor("primary.main")};
                    }
                }
            }
        `,
    },
};

const RecentShipmentSummaryCard: FC<Props> = ({
    recentShipmentItem,
    extendedStyles,
    hasAccessToInvoiceHistory,
    session,
}) => {
    const [styles] = React.useState(() => mergeToNew(recentShipmentSummaryCardStyles, extendedStyles));

    const shipmentDateDisplay = getLocalizedDateTime({
        dateTime: new Date(recentShipmentItem.shipmentDate!),
        language: session?.language,
        options: {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        },
    });

    let shipmentOrderDateDisplay = "";
    if (recentShipmentItem.orderDate) {
        shipmentOrderDateDisplay = getLocalizedDateTime({
            dateTime: new Date(recentShipmentItem.orderDate),
            language: session?.language,
            options: {
                year: "numeric",
                month: "numeric",
                day: "numeric",
            },
        });
    }

    const { customerNumber, invoiceDetailsPageLink, impersonation } = useAppSelector(state => ({
        customerNumber: getBillToCustomerNumber(state),
        invoiceDetailsPageLink: getPageLinkByPageType(state, "InvoiceDetailsPage")?.url,
        impersonation: trackAsImpersonation(state),
    }));

    return (
        <>
            <DataTableCell translate="no" {...styles.dataCell} data-th={translate("Shipment Date")}>
                <SmallHeadingAndText heading="" text={shipmentDateDisplay} extendedStyles={styles.itemHeadingAndText} />
            </DataTableCell>
            <DataTableCell translate="no" {...styles.dataCell} data-th={translate("Invoice Number")}>
                {hasAccessToInvoiceHistory ? (
                    <InvoiceDetailPageTypeLink
                        title={recentShipmentItem.invoiceNumber}
                        invoiceNumber={recentShipmentItem.invoiceNumber}
                        otherLinkProps={{
                            onClick: () =>
                                trackGaShipmentDetailsEvent(
                                    recentShipmentItem.invoiceNumber,
                                    impersonation,
                                    customerNumber,
                                    `${invoiceDetailsPageLink}?invoiceNumber=${recentShipmentItem.invoiceNumber}`,
                                ),
                        }}
                    />
                ) : (
                    <SmallHeadingAndText
                        heading=""
                        text={recentShipmentItem.invoiceNumber}
                        extendedStyles={styles.itemHeadingAndText}
                    />
                )}
            </DataTableCell>
            <DataTableCell {...styles.dataCell} translate="no" data-th={translate("Invoice Amount")}>
                <SmallHeadingAndText
                    heading=""
                    text={recentShipmentItem.invoiceAmount}
                    extendedStyles={styles.itemHeadingAndText}
                />
            </DataTableCell>
            <DataTableCell translate="no" {...styles.dataCell} data-th={translate("PO Number")}>
                <SmallHeadingAndText
                    heading=""
                    text={recentShipmentItem.poNumber}
                    extendedStyles={styles.itemHeadingAndText}
                />
            </DataTableCell>
            <DataTableCell translate="no" {...styles.dataCell} data-th={translate("Tracking Number")}>
                {recentShipmentItem?.trackingUrl?.length > 0 ? (
                    <Link color="secondary" href={recentShipmentItem.trackingUrl}>
                        {recentShipmentItem.trackingNumber}
                    </Link>
                ) : (
                    <SmallHeadingAndText
                        heading=""
                        text={recentShipmentItem.trackingNumber}
                        extendedStyles={styles.itemHeadingAndText}
                    />
                )}
            </DataTableCell>
            <DataTableCell translate="no" {...styles.dataCell} data-th={translate("Order Number")}>
                <OrderDetailPageTypeLink
                    title={recentShipmentItem.orderNumber}
                    orderNumber={recentShipmentItem.orderNumber}
                />
            </DataTableCell>
            <DataTableCell translate="no" {...styles.dataCell} data-th={translate("Order Date")}>
                <SmallHeadingAndText
                    heading=""
                    text={shipmentOrderDateDisplay}
                    extendedStyles={styles.itemHeadingAndText}
                />
            </DataTableCell>
        </>
    );
};

export default RecentShipmentSummaryCard;
